import { milestoneDateDisplay, milestoneHasValue } from 'helpers/formatDateOnly';

import { GlassCard } from '../../GlassCard/GlassCard';
import type { AdvisorDashboardRewards, GlassCardSpan, GlassCardVariant } from '../../types';

import './_achievementsCard.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18n-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface AchievementsPanelData {
  loading: boolean;
  loadError: string | null;
  rewards: AdvisorDashboardRewards | null;
}

interface AchievementsCardProps {
  span?: GlassCardSpan;
  variant?: GlassCardVariant;
  /** Forwarded to `GlassCard` when the middle stack uses extra content inset (e.g. carousel chevrons). */
  addContentGutter?: boolean;
  lightTheme?: boolean;
  /** Loaded by GlassWidgetPanel (advisor dashboard rewards slice). */
  achievements: AchievementsPanelData;
}

function formatRevenueUsd(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function revenueUsdOrNull(rewards: AdvisorDashboardRewards): number | null {
  const h = rewards.highlight?.value;
  if (h !== undefined && !Number.isNaN(h)) return h;
  const r = rewards.totals?.revenue_usd;
  if (r !== undefined && !Number.isNaN(r)) return r;
  return null;
}

/** True when revenue is a non-zero number (0 is treated as no row). */
function hasReportedRevenue(rewards: AdvisorDashboardRewards): boolean {
  const n = revenueUsdOrNull(rewards);
  return n !== null && !Number.isNaN(n) && n !== 0;
}

/** True when at least one achievements stat row should render (success path, `rewards` non-null). */
export function achievementsHasAnyStatRow(rewards: AdvisorDashboardRewards): boolean {
  if (milestoneHasValue(rewards.last_entry_display, rewards.last_entry_at)) return true;
  // if (rewards.total_entries > 0) return true;
  if (hasReportedRevenue(rewards)) return true;
  return false;
}

/** Whether the achievements card should be mounted (loading/error always show; else need data rows). */
export function achievementsCardShouldMount(panel: AchievementsPanelData): boolean {
  if (panel.loading) return true;
  if (panel.loadError) return true;
  if (panel.rewards === null) return false;
  return achievementsHasAnyStatRow(panel.rewards);
}

const headerExtra = (
  <span className="achievements-card__header-star" aria-hidden>
    <FontAwesomeIcon icon={['fas', 'treasure-chest']} />
  </span>
);

const rewardsLink = '/main/rewards';

export function AchievementsCard({
  span,
  variant,
  addContentGutter,
  lightTheme = false,
  achievements,
}: AchievementsCardProps) {
  const { loading, loadError, rewards } = achievements;

  const hasLastBooking = rewards !== null;
  const lastBookingDisplay = milestoneHasValue(rewards?.last_entry_display, rewards?.last_entry_at)
    ? milestoneDateDisplay(rewards?.last_entry_display, rewards?.last_entry_at)
    : (i18n.translate('glassWidgets.achievements.notAvailable') as string);

  const hasContestEntries = rewards !== null;
  const contestEntriesDisplay = rewards?.total_entries ?? 0;

  const revenueAmount = rewards !== null ? revenueUsdOrNull(rewards) : null;
  const hasRevenue = rewards !== null && revenueUsdOrNull(rewards) !== null;
  const highlightLabel = rewards?.highlight?.label?.trim();
  const totalRevenueLabel =
    highlightLabel !== undefined && highlightLabel !== ''
      ? highlightLabel
      : (i18n.translate('glassWidgets.achievements.totalRevenue') as string);
  const totalRevenueDisplay = hasRevenue && revenueAmount !== null ? formatRevenueUsd(revenueAmount) : '';

  const hasStatsList = hasLastBooking || hasContestEntries || hasRevenue;
  const headerWrapper = (children: ReactNode) => (
    <Link className="achievements-card__header-link" to={rewardsLink}>
      {children}
    </Link>
  );

  return (
    <GlassCard
      title={i18n.translate('glassWidgets.achievements.title') as string}
      headerExtra={headerExtra}
      headerWrapper={headerWrapper}
      span={span}
      variant={variant}
      addContentGutter={addContentGutter}
      lightTheme={lightTheme}
      className="achievements-card"
    >
      {loading && (
        <p className="achievements-card__loading">{i18n.translate('glassWidgets.achievements.loading') as string}</p>
      )}

      {!loading && loadError && <p className="achievements-card__loading">{loadError}</p>}

      {!loading && !loadError && hasStatsList && (
        <div className="achievements-card__content">
          <Link className="achievements-card__content-link" to={rewardsLink}>
            <ul className="achievements-card__stats">
              {hasLastBooking && (
                <li className="achievements-card__stat-item">
                  <span className="achievements-card__stat-label">
                    {i18n.translate('glassWidgets.achievements.lastBooking') as string}
                  </span>
                  <span className="achievements-card__stat-value">{lastBookingDisplay}</span>
                </li>
              )}
              {hasContestEntries && (
                <li className="achievements-card__stat-item">
                  <span className="achievements-card__stat-label">
                    {i18n.translate('glassWidgets.achievements.contestEntries') as string}
                  </span>
                  <span className="achievements-card__stat-value">{contestEntriesDisplay}</span>
                </li>
              )}
              {hasRevenue && (
                <li className="achievements-card__stat-item">
                  <span className="achievements-card__stat-label">{totalRevenueLabel}</span>
                  <span className="achievements-card__stat-value">{totalRevenueDisplay}</span>
                </li>
              )}
            </ul>
          </Link>
        </div>
      )}
    </GlassCard>
  );
}
